import { FORM_CONTEXT_KEYS, type FormTestInput, type PdfTarget } from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { OP_LABEL, needsValue, takesList } from './conditions';
import type { QuestionInfo } from './conditions';
import { fromList, toList } from './EditorParts';
import {
  blankPdf,
  blankSource,
  blankTest,
  CONTEXT_LABEL,
  MANUAL_KINDS,
  move,
  parseScalar,
  parseScalars,
  PDF_KINDS,
  pdfKind,
  SOURCE_KINDS,
  TEST_KINDS,
  type FieldDraft,
  type PdfKind,
  type Scalar,
  type SourceDraft,
} from './insurer-form';

/** The insurer-form editor's parts (task 96): tests, sources and where a field goes on the PDF. */

const TEST_OPS = ['eq', 'ne', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'contains', 'answered', 'not_answered'] as const; // prettier-ignore
const TEST_LABEL: Record<string, string> = Object.fromEntries(SOURCE_KINDS);

/** The id of the datalist of every question key, rendered once by the editor. */
export const QUESTIONS_LIST = 'if-question-keys';

/** A value to compare with: the question's options where it has them, else typed (numbers and true/false). */
function ValueBox({
  op,
  value,
  question,
  disabled,
  onChange,
}: {
  op: string;
  value: Scalar | Scalar[] | undefined;
  question?: QuestionInfo;
  disabled: boolean;
  onChange: (v: Scalar | Scalar[] | undefined) => void;
}) {
  if (!needsValue(op)) return null;
  const options = question?.options ?? [];
  if (takesList(op))
    return (
      <input className="input mono" aria-label="Values" disabled={disabled} value={toList((Array.isArray(value) ? value : value === undefined ? [] : [value]).map(String))} placeholder={options.length ? options.map((o) => o.value).slice(0, 4).join(', ') : 'a, b'} onChange={(e) => onChange(parseScalars(e.target.value))} /> // prettier-ignore
    );
  if (options.length && (op === 'eq' || op === 'ne' || op === 'contains'))
    return (
      <select className="input" aria-label="Value" disabled={disabled} value={value === undefined ? '' : String(value)} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ); // prettier-ignore
  return (
    <input className="input" aria-label="Value" disabled={disabled} value={value === undefined || Array.isArray(value) ? '' : String(value)} onChange={(e) => onChange(parseScalar(e.target.value))} /> // prettier-ignore
  );
}

/** One yes/no/don't-know test about the business, nested as deep as the form needs. */
export function FormTestEditor({
  test,
  onChange,
  questions,
  disabled,
  depth = 0,
  kindless = false,
}: {
  test: FormTestInput;
  onChange: (t: FormTestInput) => void;
  questions: Map<string, QuestionInfo>;
  disabled: boolean;
  depth?: number;
  /** The kind is chosen elsewhere (a field's source), so not offered again here. */
  kindless?: boolean;
}) {
  const kind = kindless ? null : (
    <select className="input" aria-label="Test" disabled={disabled} value={test.from} onChange={(e) => onChange(blankTest(e.target.value as (typeof TEST_KINDS)[number], test))}>
      {TEST_KINDS.map((k) => <option key={k} value={k}>{TEST_LABEL[k]!.replace(' (yes/no)', '')}</option>)}
    </select>
  ); // prettier-ignore

  if (test.from === 'control')
    return (
      <div className="ft-row">
        {kind}
        <input className="input mono" aria-label="Controls" disabled={disabled} value={toList(test.controls)} placeholder="e.g. IA-01, IA-02" onChange={(e) => onChange({ ...test, controls: fromList(e.target.value) })} />
        <select className="input" aria-label="Combine" disabled={disabled} value={test.combine ?? 'all'} onChange={(e) => onChange({ ...test, combine: e.target.value as 'all' | 'any' })}>
          <option value="all">all of them</option>
          <option value="any">any of them</option>
        </select>
        <select className="input" aria-label="In place" disabled={disabled} value={test.yes_at ?? 1} onChange={(e) => onChange({ ...test, yes_at: Number(e.target.value) as 1 | 0.5 })}>
          <option value={1}>fully in place</option>
          <option value={0.5}>at least partly</option>
        </select>
      </div>
    ); // prettier-ignore

  if (test.from === 'answer') {
    const q = questions.get(test.question);
    return (
      <div className="ft-row">
        {kind}
        <input className="input mono" aria-label="Question" list={QUESTIONS_LIST} disabled={disabled} value={test.question} placeholder="Question key" title={q?.text} onChange={(e) => onChange({ ...test, question: e.target.value })} />
        <input className="input mono ft-path" aria-label="Part of the answer" disabled={disabled} value={test.path ?? ''} placeholder="part (optional)" onChange={(e) => onChange({ ...test, path: e.target.value || undefined })} />
        <select className="input" aria-label="Comparison" disabled={disabled} value={test.op} onChange={(e) => onChange({ ...test, op: e.target.value as typeof test.op, value: needsValue(e.target.value) ? test.value : undefined })}>
          {TEST_OPS.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
        </select>
        <ValueBox op={test.op} value={test.value} question={q} disabled={disabled} onChange={(value) => onChange({ ...test, value })} />
      </div>
    ); // prettier-ignore
  }

  if (test.from === 'detail')
    return (
      <div className="ft-row">
        {kind}
        <select className="input" aria-label="Company detail" disabled={disabled} value={test.key} onChange={(e) => onChange({ ...test, key: e.target.value as typeof test.key })}>
          {FORM_CONTEXT_KEYS.map((k) => <option key={k} value={k}>{CONTEXT_LABEL[k]}</option>)}
        </select>
        <select className="input" aria-label="Comparison" disabled={disabled} value={test.op} onChange={(e) => onChange({ ...test, op: e.target.value as typeof test.op })}>
          {TEST_OPS.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
        </select>
        <ValueBox op={test.op} value={test.value} disabled={disabled} onChange={(value) => onChange({ ...test, value })} />
      </div>
    ); // prettier-ignore

  if (test.from === 'not')
    return (
      <div className="ft-group">
        {kind && <div className="ft-row">{kind}</div>}
        <div className="ft-nested">
          <FormTestEditor test={test.of} onChange={(of) => onChange({ ...test, of })} questions={questions} disabled={disabled} depth={depth + 1} />
        </div>
      </div>
    ); // prettier-ignore

  return (
    <div className="ft-group">
      {kind && <div className="ft-row">{kind}</div>}
      <ol className="ft-nested" aria-label={test.from === 'all' ? 'All of' : 'Any of'}>
        {test.of.map((t, i) => (
          <li key={i} className="ft-item">
            <FormTestEditor test={t} onChange={(x) => onChange({ ...test, of: test.of.map((y, n) => (n === i ? x : y)) })} questions={questions} disabled={disabled} depth={depth + 1} />
            {!disabled && test.of.length > 1 && (
              <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove test ${i + 1}`} onClick={() => onChange({ ...test, of: test.of.filter((_, n) => n !== i) })}>
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
      </ol>
      {!disabled && depth < 3 && (
        <button type="button" className="btn btn-sm" onClick={() => onChange({ ...test, of: [...test.of, { from: 'control', controls: [] }] })}>
          <Plus size={14} /> Add a test
        </button>
      )}
    </div>
  ); // prettier-ignore
}

/** Answer value → form value, e.g. partly → partial. */
function ValueMap({
  map,
  onChange,
  disabled,
}: {
  map: Record<string, Scalar> | undefined;
  onChange: (m: Record<string, Scalar> | undefined) => void;
  disabled: boolean;
}) {
  const rows = Object.entries(map ?? {});
  const set = (next: [string, Scalar][]) => onChange(next.length ? Object.fromEntries(next) : undefined);
  return (
    <div className="ft-map">
      {rows.map(([from, to], i) => (
        <div key={i} className="ft-row">
          <input className="input mono" aria-label={`Answer value ${i + 1}`} disabled={disabled} value={from} onChange={(e) => set(rows.map((r, n) => (n === i ? [e.target.value, r[1]] : r)))} />
          <span className="muted">→</span>
          <input className="input mono" aria-label={`Form value ${i + 1}`} disabled={disabled} value={String(to)} onChange={(e) => set(rows.map((r, n) => (n === i ? [r[0], parseScalar(e.target.value)] : r)))} />
          {!disabled && (
            <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove mapping ${i + 1}`} onClick={() => set(rows.filter((_, n) => n !== i))}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" className="btn btn-sm" onClick={() => set([...rows, ['', '']])}>
          <Plus size={14} /> Map a value
        </button>
      )}
      {!rows.length && <span className="hint">No mapping: the answer is copied as it is.</span>}
    </div>
  ); // prettier-ignore
}

/** One place a field's answer can come from. */
function SourceEditor({
  source,
  field,
  onChange,
  questions,
  disabled,
}: {
  source: SourceDraft;
  field: FieldDraft;
  onChange: (s: SourceDraft) => void;
  questions: Map<string, QuestionInfo>;
  disabled: boolean;
}) {
  const isTest = (TEST_KINDS as readonly string[]).includes(source.from);
  const offered = SOURCE_KINDS.filter(([k]) =>
    (TEST_KINDS as readonly string[]).includes(k) ? field.type === 'yes_no' : k === 'checklist' ? field.type === 'multi_choice' : true,
  ); // prettier-ignore
  return (
    <div className="stack-sm">
      <select className="input" aria-label="Comes from" disabled={disabled} value={source.from} onChange={(e) => onChange(blankSource(e.target.value as SourceDraft['from'], source))}>
        {offered.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        {!offered.some(([k]) => k === source.from) && <option value={source.from}>{TEST_LABEL[source.from]} (doesn’t fit this type)</option>}
      </select>
      {isTest && <FormTestEditor test={source as FormTestInput} onChange={(t) => onChange(t as SourceDraft)} questions={questions} disabled={disabled} kindless />}
      {source.from === 'value' && (
        <>
          <div className="ft-row">
            <input className="input mono" aria-label="Question" list={QUESTIONS_LIST} disabled={disabled} value={source.question} placeholder="Question key" title={questions.get(source.question)?.text} onChange={(e) => onChange({ ...source, question: e.target.value })} />
            <input className="input mono ft-path" aria-label="Part of the answer" disabled={disabled} value={source.path ?? ''} placeholder="part (optional)" onChange={(e) => onChange({ ...source, path: e.target.value || undefined })} />
          </div>
          {questions.get(source.question) && <span className="hint">{questions.get(source.question)!.text}</span>}
          <ValueMap map={source.map} onChange={(map) => onChange({ ...source, map })} disabled={disabled} />
        </>
      )}
      {source.from === 'context' && (
        <select className="input" aria-label="Company detail" disabled={disabled} value={source.key} onChange={(e) => onChange({ ...source, key: e.target.value as typeof source.key })}>
          {FORM_CONTEXT_KEYS.map((k) => <option key={k} value={k}>{CONTEXT_LABEL[k]}</option>)}
        </select>
      )}
      {source.from === 'checklist' && <span className="hint">Each option above is ticked by its own test.</span>}
      {MANUAL_KINDS.has(source.from) && (
        <input className="input" aria-label="Note" disabled={disabled} value={'note' in source ? (source.note ?? '') : ''} placeholder="Note for the broker (optional)" onChange={(e) => onChange({ ...source, note: e.target.value || undefined } as SourceDraft)} />
      )}
    </div>
  ); // prettier-ignore
}

/** Where a field's answer comes from: the first source that gives one is used. */
export function SourcesEditor({
  field,
  onChange,
  questions,
  disabled,
}: {
  field: FieldDraft;
  onChange: (list: SourceDraft[]) => void;
  questions: Map<string, QuestionInfo>;
  disabled: boolean;
}) {
  const list = (Array.isArray(field.source) ? field.source : [field.source]) as SourceDraft[];
  return (
    <div className="stack-sm">
      <ol className="source-list" aria-label="Sources">
        {list.map((s, i) => (
          <li key={i} className="source-item" aria-label={`Source ${i + 1}`}>
            <div className="row-between">
              <span className="eyebrow">{i === 0 ? 'From' : 'Else from'}</span>
              {!disabled && list.length > 1 && (
                <span className="row">
                  <button type="button" className="btn btn-icon btn-ghost" aria-label={`Move source ${i + 1} up`} disabled={i === 0} onClick={() => onChange(move(list, i, -1))}><ArrowUp size={14} /></button>
                  <button type="button" className="btn btn-icon btn-ghost" aria-label={`Move source ${i + 1} down`} disabled={i === list.length - 1} onClick={() => onChange(move(list, i, 1))}><ArrowDown size={14} /></button>
                  <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove source ${i + 1}`} onClick={() => onChange(list.filter((_, n) => n !== i))}><Trash2 size={14} /></button>
                </span>
              )}
            </div>
            <SourceEditor source={s} field={field} onChange={(x) => onChange(list.map((y, n) => (n === i ? x : y)))} questions={questions} disabled={disabled} />
          </li>
        ))}
      </ol>
      {!disabled && list.length < 5 && (
        <button type="button" className="btn btn-sm" onClick={() => onChange([...list, { from: 'unmapped' }])}>
          <Plus size={14} /> Add a fallback
        </button>
      )}
    </div>
  ); // prettier-ignore
}

/** Name → widget rows, e.g. a value's checkbox or a column's text box. */
function WidgetMap({
  label,
  map,
  onChange,
  disabled,
  keyLabel,
}: {
  label: string;
  map: Record<string, string>;
  onChange: (m: Record<string, string>) => void;
  disabled: boolean;
  keyLabel: string;
}) {
  const rows = Object.entries(map);
  const set = (next: [string, string][]) => onChange(Object.fromEntries(next));
  return (
    <div className="ft-map" aria-label={label}>
      {rows.map(([k, w], i) => (
        <div key={i} className="ft-row">
          <input className="input mono ft-path" aria-label={`${keyLabel} ${i + 1}`} disabled={disabled} value={k} onChange={(e) => set(rows.map((r, n) => (n === i ? [e.target.value, r[1]] : r)))} />
          <span className="muted">→</span>
          <input className="input mono" aria-label={`Widget for ${k || `row ${i + 1}`}`} disabled={disabled} value={w} placeholder="PDF field name" onChange={(e) => set(rows.map((r, n) => (n === i ? [r[0], e.target.value] : r)))} />
          {!disabled && (
            <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove ${k || `row ${i + 1}`}`} onClick={() => set(rows.filter((_, n) => n !== i))}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" className="btn btn-sm" onClick={() => set([...rows, ['', '']])}>
          <Plus size={14} /> Add
        </button>
      )}
    </div>
  ); // prettier-ignore
}

/** Where a field goes on the insurer's own fillable PDF: the AcroForm widget names, mapped by hand. */
export function PdfTargetEditor({
  target,
  field,
  onChange,
  disabled,
}: {
  target: PdfTarget | undefined;
  field: FieldDraft;
  onChange: (t: PdfTarget | undefined) => void;
  disabled: boolean;
}) {
  const kind = pdfKind(target);
  return (
    <div className="stack-sm">
      <select className="input" aria-label="On the PDF" disabled={disabled} value={kind} onChange={(e) => onChange(blankPdf(e.target.value as PdfKind, field))}>
        {PDF_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      {typeof target === 'string' && (
        <input className="input mono" aria-label="Text box" disabled={disabled} value={target} placeholder="PDF field name, e.g. Text Field 3" onChange={(e) => onChange(e.target.value)} />
      )}
      {target && typeof target === 'object' && 'lines' in target && (
        <input className="input mono" aria-label="Text boxes, a line each" disabled={disabled} value={toList(target.lines)} placeholder="a 6, a 7" onChange={(e) => onChange({ lines: fromList(e.target.value) })} />
      )}
      {target && typeof target === 'object' && 'radio' in target && (
        <>
          <input className="input mono" aria-label="Radio group" disabled={disabled} value={target.radio} placeholder="PDF radio group, e.g. b1 Group2" onChange={(e) => onChange({ ...target, radio: e.target.value })} />
          <WidgetMap label="Export state for each value" keyLabel="Value" map={target.states} onChange={(states) => onChange({ ...target, states })} disabled={disabled} />
        </>
      )}
      {target && typeof target === 'object' && 'boxes' in target && (
        <>
          <WidgetMap label="Box for each value" keyLabel="Value" map={target.boxes} onChange={(boxes) => onChange({ ...target, boxes })} disabled={disabled} />
          <div className="two">
            <input className="input mono" aria-label="Ticked whenever filled" disabled={disabled} value={target.tick ?? ''} placeholder="Tick box when filled (optional)" onChange={(e) => onChange({ ...target, tick: e.target.value || undefined })} />
            <input className="input mono" aria-label="Other value box" disabled={disabled} value={target.other ?? ''} placeholder="Box for any other value (optional)" onChange={(e) => onChange({ ...target, other: e.target.value || undefined })} />
          </div>
        </>
      )}
      {target && typeof target === 'object' && 'columns' in target && (
        <WidgetMap label="Box for each column" keyLabel="Column" map={target.columns} onChange={(columns) => onChange({ columns })} disabled={disabled} />
      )}
      {target && typeof target === 'object' && 'table' in target && (
        <>
          <input className="input mono" aria-label="Rows matched by column" disabled={disabled} value={target.by ?? ''} placeholder="Match rows by this column (empty: by position 0, 1, …)" onChange={(e) => onChange({ ...target, by: e.target.value || undefined })} />
          {Object.entries(target.table).map(([row, cols], i) => (
            <div key={i} className="source-item">
              <div className="ft-row">
                <input className="input mono ft-path" aria-label={`Row ${i + 1}`} disabled={disabled} value={row} onChange={(e) => onChange({ ...target, table: Object.fromEntries(Object.entries(target.table).map(([r, c], n) => (n === i ? [e.target.value, c] : [r, c]))) })} />
                {!disabled && (
                  <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove row ${row}`} onClick={() => onChange({ ...target, table: Object.fromEntries(Object.entries(target.table).filter((_, n) => n !== i)) })}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <WidgetMap label={`Row ${row} columns`} keyLabel="Column" map={cols} onChange={(c) => onChange({ ...target, table: { ...target.table, [row]: c } })} disabled={disabled} />
            </div>
          ))}
          {!disabled && (
            <button type="button" className="btn btn-sm" onClick={() => onChange({ ...target, table: { ...target.table, [String(Object.keys(target.table).length)]: {} } })}>
              <Plus size={14} /> Add a row
            </button>
          )}
        </>
      )}
    </div>
  ); // prettier-ignore
}
